import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Pagination from "../../../components/Pagination";
import styles from "./index.module.css";
import SearchBar from "../../../components/SearchBar";
import { useDispatch, useSelector } from "react-redux";
import FilterDropdown from "../../../components/FilterDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { getActiveCourses, getAllCourses } from "../../../redux/features/courses/coursesService";
import NoResultsFound from "../../../components/NoResultsFound";
import TableSkeleton from "../../../components/Skeleton";
import { getStudentRegistrationRequestsByCoordinator } from "../../../redux/features/studentRegistration/studentRegistrationService";
import { useNavigate } from "react-router-dom";

const Status = [
    { id: 'Approved', name: 'Approved' },
    { id: 'Reject', name: 'Reject' },
    { id: 'Pending', name: 'Pending' },
];

const RegistrationRequest = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const courseDropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchValue, setSearchValue] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const { courses } = useSelector((state) => state.course.activeCourses);

    const { isDataLoading, regRequests, pageCount } = useSelector(
        (state) => state.studentRegRequests.coordinatorRegistrationRequests
    );

    const totalPages = pageCount || 1;

    const fetchRegistrationRequests = useCallback(
        (filters = {}) => {
            const params = {
                search: searchTerm,
                course_id: selectedCourse && selectedCourse,
                status: selectedStatus && selectedStatus,
                skip: currentPage - 1,
                limit: 10,
                ...filters,
            };
            dispatch(getStudentRegistrationRequestsByCoordinator(params));
        },
        [dispatch, searchTerm, selectedCourse, selectedStatus, currentPage]
    );

    useEffect(() => {
        fetchRegistrationRequests();
    }, [fetchRegistrationRequests]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleSearch = (searchTerm) => {
        setSearchTerm(searchTerm);
        setCurrentPage(1);
        fetchRegistrationRequests({ search: searchTerm });
    };

    const handleSelect = (option) => {
        setSelectedCourse(option.id);
        setCurrentPage(1);
        fetchRegistrationRequests({ course_or_program_id: option.id });
    };

    const handleStatus = (option) => {
        setSelectedStatus(option.id);
        setCurrentPage(1);
        fetchRegistrationRequests({ status: option.id });
    };

    const handleClearFilters = () => {
        setCurrentPage(1);
        setSelectedCourse(null);
        setSelectedStatus(null);
        setSearchTerm("");
        setSearchValue("");
        if (courseDropdownRef.current) {
            courseDropdownRef.current.clearSelection();
        }
        if (statusDropdownRef.current) {
            statusDropdownRef.current.clearSelection();
        }
        fetchRegistrationRequests({
            search: null,
            course_or_program_id: null,
            status: null,
        });
    };

    useEffect(() => {
        dispatch(getActiveCourses({}));
    }, [dispatch]);

    
    const formattedCourses = useMemo(() => {
        return courses.map(course => ({
            id: course.id,
            name: course.title
        }));
    }, [courses]);

    const openRequest = (id) => {
        navigate(`/admin/requests/${id}`)
    }

    return (
        <div className={`search-card`}>
            <div>
                <div className={`search-card-header  ${styles.searchHeader}`}>
                    <SearchBar
                        value={searchValue}
                        onSearch={handleSearch}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <div className={styles.headerRight}>
                        <div className={styles.headerDropdownSec}>
                            <FilterDropdown
                                ref={courseDropdownRef}
                                options={formattedCourses}
                                placeholder="Sort by Course/Program"
                                onSelect={handleSelect}
                            />
                            <FilterDropdown
                                ref={statusDropdownRef}
                                options={Status}
                                placeholder="Sort by Status"
                                onSelect={handleStatus}
                            />
                        </div>
                        <div className={styles.clearButton} onClick={handleClearFilters}>
                            <FontAwesomeIcon icon={faCircleXmark} /> <span>Clear All</span>
                        </div>
                    </div>
                </div>
                <div className={`search-card-body`}>
                    {isDataLoading ?
                        <TableSkeleton columns={["Full Name", "Email", "Phone Number", "Course", "Status"]} rowCount={10} height={20} />
                        : regRequests?.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Full Name</th>
                                            <th>Email</th>
                                            <th>Phone Number</th>
                                            <th>Course</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {regRequests?.map((request) => (
                                            <tr key={request?.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {request?.full_name}
                                                </td>
                                                <td>{request?.email}</td>
                                                <td>{request?.phone_number}</td>
                                                <td>{request?.course.title}</td>
                                                <td><span className={`status-text-label ${request?.status?.toLowerCase()}`}>{request?.status}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
            </div>
            <div className={`search-card-footer`}>
                {regRequests?.length > 0 && (
                    <div className={styles.paginationDiv}>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistrationRequest;